import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  filter,
  take,
} from 'rxjs/operators';
import { CompaniesFacade } from '../../store/companies/companies.facade';
import { ReferenceFacade } from '../../store/reference/reference.facade';
import { CompanyCreateRequest, SalesforceCompany } from '../../core';
import { CompanyFormComponent } from './company-form/company-form.component';
import { CHECK_LAYOUT_TYPE_TO_ID_MAP } from './company-form.utils';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-add-company',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CompanyFormComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './add-company.component.html',
  styleUrls: ['./add-company.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCompanyComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly companiesFacade = inject(CompaniesFacade);
  private readonly referenceFacade = inject(ReferenceFacade);
  private readonly authService = inject(AuthService);
  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();

  // Form
  companyForm!: FormGroup;

  // Search state (Salesforce search)
  searchTerm = '';
  isFromSalesforce = false;
  selectedCustomerType = '';
  selectedSubprocessorConsent = '';

  // Reactive state from facades
  readonly salesforceResults$ = this.companiesFacade.salesforceResults$;
  readonly salesforceLoading$ = this.companiesFacade.salesforceLoading$;
  readonly saving$ = this.companiesFacade.saving$;
  readonly saveError$ = this.companiesFacade.saveError$;

  // Reference data from facade
  readonly printSites$ = this.referenceFacade.activePrintSites$;
  readonly customerTypes$ = this.referenceFacade.activeCustomerTypes$;

  ngOnInit(): void {
    this.initForm();
    this.loadReferenceData();
    this.setupSearchSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.companyForm = this.fb.group({
      arNumber: ['', [Validators.required]],
      name: ['', [Validators.required]],
      customerTypeId: [null],
      preferPrintSiteId: [null],
      checkLayoutType: ['', Validators.required],
      backOfficeSort: [false],
      packingDetailsDistribution: [false],
      ultibank: [false],
      custom: [false],
      shippingOverwrite: [false],
      manualCheck: [false],
      groupRequests: [false],
      customProcessFileName: ['', [Validators.maxLength(255)]],
      comments: ['', [Validators.maxLength(2000)]],
    });
  }

  private loadReferenceData(): void {
    this.referenceFacade.loadAllReferenceData();
  }

  private setupSearchSubscription(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(query => {
        if (query.trim() && query.trim().length >= 3) {
          this.companiesFacade.searchSalesforce(query);
        }
      });
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    this.searchSubject.next(this.searchTerm);
  }

  onSelectCompany(company: SalesforceCompany): void {
    this.isFromSalesforce = true;
    this.selectedCustomerType = company.customerType ?? '';
    this.selectedSubprocessorConsent = company.subprocessorConsent ?? '';

    this.companyForm.patchValue({
      arNumber: company.arNumber,
      name: company.companyName,
    });

    this.customerTypes$
      .pipe(
        filter(types => types.length > 0),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(types => {
        const match = types.find(
          t =>
            t.description.toLowerCase() ===
            this.selectedCustomerType.toLowerCase()
        );
        if (match) {
          this.companyForm.patchValue({ customerTypeId: match.customerTypeId });
        }
      });

    this.searchTerm = '';
  }

  onSave(): void {
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched();
      return;
    }

    const formValue = this.companyForm.value;

    this.authService
      .getCurrentUser()
      .pipe(take(1))
      .subscribe(user => {
        const createRequest: CompanyCreateRequest = {
          arNumber: formValue.arNumber,
          companyName: formValue.name,
          customerTypeId: formValue.customerTypeId ?? null,
          outsourceConsent: true,
          preferPrintSiteId: formValue.preferPrintSiteId ?? 0,
          isOutsourceOnly: false,
          isUltiBank: formValue.ultibank ?? false,
          isPackingDetailDistribution:
            formValue.packingDetailsDistribution ?? false,
          checkLayoutId:
            CHECK_LAYOUT_TYPE_TO_ID_MAP[formValue.checkLayoutType] ?? 1,
          isBackOfficeSort: formValue.backOfficeSort ?? false,
          isCustom: formValue.custom ?? false,
          isShippingUpCharge: formValue.shippingOverwrite ?? false,
          customProcessFileName: formValue.customProcessFileName ?? null,
          comments: formValue.comments ?? null,
          isManualCheck: formValue.manualCheck ?? false,
          isGroupRequests: formValue.groupRequests ?? false,
          createdBy: user?.userName ?? user?.email ?? '',
        };

        this.companiesFacade.createCompany(createRequest);
      });
  }

  onCancel(): void {
    this.router.navigate(['/company']);
  }

  onReset(): void {
    this.companyForm.reset({
      backOfficeSort: false,
      packingDetailsDistribution: false,
      ultibank: false,
      custom: false,
      shippingOverwrite: false,
      manualCheck: false,
      groupRequests: false,
    });

    this.isFromSalesforce = false;
    this.searchTerm = '';
    this.selectedCustomerType = '';
    this.selectedSubprocessorConsent = '';
  }

  // Form field getters for validation
  get arNumberControl() {
    return this.companyForm.get('arNumber');
  }
  get nameControl() {
    return this.companyForm.get('name');
  }

  // Check if form has unsaved changes
  get hasUnsavedChanges(): boolean {
    return this.companyForm.dirty;
  }
}
